import { createRoot } from 'react-dom/client'
import './index.css'
import * as serviceWorker from './serviceWorker'
import '@fontsource/work-sans'
import App from './App'
import { createStore, StoreProvider } from 'easy-peasy'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import {
  createGenerateClassName,
  createTheme,
  StylesProvider,
  ThemeProvider
} from '@material-ui/core/styles'
import storeModel from './model'
import './i18n'
import * as Sentry from '@sentry/react'

Sentry.init({
  dsn: 'https://9685fc5499e98d3b69b55fd7ecd0ba6d@o4506716255223808.ingest.sentry.io/4506716257320960',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [
        'api.slaapmakend.site',
        'api.slaapmakend.site.nl',
        'api.staging.slaapmakend.nl',
        'api.production.slaapmakend.nl'
      ]
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    'api.slaapmakend.site',
    'api.slaapmakend.site.nl',
    'api.staging.slaapmakend.nl',
    'api.production.slaapmakend.nl',
    'staging.slaapmakend.site'
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
})

const store = createStore(storeModel)

const theme = createTheme({
  typography: {
    fontFamily: '"Work Sans"'
  },
  /**
   * Focus ripples are turned off application-wide to work around a crash in
   * Material-UI v4's ButtonBase.
   *
   * ButtonBase runs this effect on mount:
   *
   *   useEffect(() => {
   *     if (focusVisible && focusRipple && !disableRipple) {
   *       rippleRef.current.pulsate()   // <- no null check
   *     }
   *   }, [disableRipple, focusRipple, focusVisible])
   *
   * The TouchRipple that populates `rippleRef` is only mounted after a
   * separate `setMountedState(true)` effect, which is declared *later* in the
   * same component and therefore commits *after* the one above. When a button
   * is focused during its very first commit — which is exactly what a Dialog's
   * focus trap does to the buttons in DialogActions — `rippleRef.current` is
   * still null and the whole React tree unmounts with
   * "null is not an object (evaluating 'rippleRef.current.pulsate')".
   *
   * Opening any confirmation dialog could take the page down with it; the
   * date-edit dialog on a session note was the reported case (SLAAP-320).
   *
   * `disableFocusRipple` short-circuits that condition. Buttons keep their
   * click ripple and their focus outline, so only the ripple animation on
   * keyboard focus is lost. Button, Fab, IconButton and Tab are the only
   * components that enable `focusRipple` by default.
   */
  props: {
    MuiButton: { disableFocusRipple: true },
    MuiIconButton: { disableFocusRipple: true },
    MuiFab: { disableFocusRipple: true },
    MuiTab: { disableFocusRipple: true }
  }
})

/**
 * IMPORTANT: dependencies might use their own classname generator. In order to
 * avoid conflicts with the default 'j' prefix, we use 'c' instead.
 */
const generateClassName = createGenerateClassName({
  productionPrefix: 'c'
})

const container = document.getElementById('root')
const root = createRoot(container)

root.render(
  <StylesProvider generateClassName={generateClassName}>
    <StoreProvider store={store}>
      <ThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <App />
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </StoreProvider>
  </StylesProvider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister()
